// First Mile leg drill-down — the legs behind a clicked First Mile tally. Two kinds on one
// endpoint (/first-mile-receives/): `picklist` lists PICK legs (the Pick List section's "picked"
// tally), `receive` lists inbound RECEIVE legs (the Team Metric block's GRN Marked column, narrowed
// to one owner). Same open/close + server-paginated shape as ShipmentListDrawer. The response
// `count` equals the clicked tally.
import { Drawer, Pagination, Table, Tag, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import React, { useEffect, useState } from "react";
import { getPIOrderStatusLabel } from "../../../../helpers/misc_helper";
import FlagPills from "./flag_pills";
import { FIRST_MILE_DOCK_LABEL } from "../first_mile_meta";
import {
  FirstMileDrilldownRow,
  FirstMilePickRow,
  FirstMileReceiveRow,
  listFirstMileReceives,
} from "../control_tower.api";
import { FirstMileCardMetricKey, FirstMileDrilldownRequest, FirstMileWarehouseKey } from "../types";

interface Props {
  request: FirstMileDrilldownRequest | null;
  dispatchFrom: string;
  dispatchTo: string;
  onClose: () => void;
}

const METRIC_TITLE: Record<FirstMileCardMetricKey, string> = {
  total_shipments: "Total Shipments",
  yet_to_pick: "Yet to Pick",
  grn_marked: "GRN Marked",
};

const dash = <span style={{ color: "#bbb" }}>—</span>;

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

// "in_transit" -> "In transit" — the leg_status values arrive snake-cased.
const humanize = (v: string) => {
  const spaced = v.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const sharedColumns: ColumnsType<FirstMileDrilldownRow> = [
  {
    title: "PO Number",
    dataIndex: "po_number",
    key: "po_number",
    render: (v: string | null, r) => (
      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <span>{v ?? "—"}</span>
        <FlagPills express={r.express} unloading={r.unloading} />
      </div>
    ),
  },
  { title: "Lead ID", dataIndex: "lead_id", key: "lead_id", render: (v) => v ?? dash },
  {
    title: "Estimate Status",
    dataIndex: "estimate_status",
    key: "estimate_status",
    render: (v) => getPIOrderStatusLabel(v),
  },
  { title: "Supplier", dataIndex: "supplier", key: "supplier", render: (v) => v ?? dash },
  {
    title: "Dock",
    dataIndex: "dock",
    key: "dock",
    render: (v: FirstMileWarehouseKey) => FIRST_MILE_DOCK_LABEL[v] ?? v,
  },
  {
    title: "Target Delivery",
    dataIndex: "target_delivery_date",
    key: "target_delivery_date",
    render: fmtDate,
  },
  {
    title: "Leg Status",
    dataIndex: "leg_status",
    key: "leg_status",
    render: (v: string | null) => (v ? <Tag>{humanize(v)}</Tag> : dash),
  },
  {
    // A stuck leg (error_message) reads red; a legitimately-waiting one (hold_reason) reads amber.
    title: "Issue",
    key: "issue",
    render: (_, r) =>
      r.error_message ? (
        <Tooltip title={r.error_message} overlayStyle={{ maxWidth: 480 }}>
          <Tag color="red" style={{ cursor: "help" }}>
            Error
          </Tag>
        </Tooltip>
      ) : r.hold_reason ? (
        <Tooltip title={r.hold_reason} overlayStyle={{ maxWidth: 480 }}>
          <Tag color="gold" style={{ cursor: "help" }}>
            On Hold
          </Tag>
        </Tooltip>
      ) : (
        dash
      ),
  },
  // SLA is always null today (rule TBD) — render the column so it's there when it lights up.
  { title: "SLA", key: "sla_state", render: () => <Tag>—</Tag> },
];

// The one column that differs between the two kinds: a pick leg is Picked, a receive leg is GRN'd.
const pickedColumn: ColumnsType<FirstMileDrilldownRow>[number] = {
  title: "Picked",
  key: "picked",
  render: (_, r) =>
    (r as FirstMilePickRow).picked ? (
      <Tag color="green">Picked</Tag>
    ) : (
      <Tag color="gold">Not picked</Tag>
    ),
};

const grnColumn: ColumnsType<FirstMileDrilldownRow>[number] = {
  title: "GRN",
  key: "grn_marked",
  render: (_, r) =>
    (r as FirstMileReceiveRow).grn_marked ? (
      <Tag color="green">Marked</Tag>
    ) : (
      <Tag color="gold">Awaiting GRN</Tag>
    ),
};

// Insert the kind-specific column where "Leg Status" ends, so both tables read the same shape.
const columnsFor = (kind: FirstMileDrilldownRequest["kind"]): ColumnsType<FirstMileDrilldownRow> => [
  ...sharedColumns.slice(0, 7),
  kind === "picklist" ? pickedColumn : grnColumn,
  ...sharedColumns.slice(7),
];

const titleFor = (request: FirstMileDrilldownRequest): string => {
  // An owner-scoped drill-down leads with the person; a section one with the board it came from.
  const parts = [request.owner ? request.owner.name : request.kind === "picklist" ? "Pick List" : "Inbound"];
  if (request.dock) parts.push(FIRST_MILE_DOCK_LABEL[request.dock]);
  return `${parts.join(" · ")} — ${METRIC_TITLE[request.metric]}`;
};

const FirstMileDrilldownDrawer: React.FC<Props> = ({ request, dispatchFrom, dispatchTo, onClose }) => {
  const [rows, setRows] = useState<FirstMileDrilldownRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // A new drill-down resets to page 1 (kept out of the fetch effect so paging doesn't reset itself).
  useEffect(() => {
    setPage(1);
  }, [request]);

  useEffect(() => {
    if (!request) return;
    let cancelled = false;
    setLoading(true);
    listFirstMileReceives({
      kind: request.kind,
      dispatch_from: dispatchFrom,
      dispatch_to: dispatchTo,
      dock: request.dock,
      assigned_to: request.owner?.id,
      yet_to_pick: request.metric === "yet_to_pick" ? true : undefined,
      // GRN Marked is the FILED half of the receive board, so the filter is the negative.
      yet_to_receive: request.metric === "grn_marked" ? false : undefined,
      page,
      page_size: pageSize,
    }).then((res) => {
      if (cancelled) return;
      setRows(res?.results ?? []);
      setTotal(res?.count ?? 0);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [request, dispatchFrom, dispatchTo, page, pageSize]);

  return (
    <Drawer
      title={request ? titleFor(request) : ""}
      width={960}
      open={!!request}
      onClose={onClose}
      destroyOnClose
    >
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={request ? columnsFor(request.kind) : []}
        dataSource={rows}
        pagination={false}
        scroll={{ x: 900 }}
      />
      <div style={{ marginTop: 16, textAlign: "right" }}>
        <Pagination
          current={page}
          pageSize={pageSize}
          total={total}
          showSizeChanger
          pageSizeOptions={["20", "50", "100", "200"]}
          showTotal={(t) =>
            `${t} ${request?.kind === "receive" ? "inbound" : "pick"} leg${t === 1 ? "" : "s"}`
          }
          onChange={(p, size) => {
            setPage(p);
            setPageSize(size);
          }}
        />
      </div>
    </Drawer>
  );
};

export default FirstMileDrilldownDrawer;
